/**
 * lbmgb-auto-track.js — universal storefront event auto-detection.
 *
 * Per ADR-4.22 (paired commit). Cross-brand fallback that fires the
 * standard ecommerce events (view_item / view_item_list / add_to_cart /
 * remove_from_cart / view_cart / begin_checkout) without per-brand
 * theme integration work. Wires into LBMGB.track.* once the brand-
 * agnostic Shopify conventions match.
 *
 * Why this exists:
 *   - Paragraph US has bespoke PDP / cart / checkout sections (pw-*)
 *     that we'd otherwise instrument one-by-one.
 *   - Wild Idol US uses a different theme altogether.
 *   - Future brand #N's theme is unknowable.
 *
 *   This module sniffs Shopify's *standard* template signals (template
 *   suffix, ProductJson script tags, cart-form actions) and fires
 *   reasonable events automatically. Brands can override the selectors
 *   via window.LBMGB.config.autoTrack.* if their theme structure differs.
 *
 * What it captures (default Shopify storefront conventions):
 *   - PDP page view → view_item (with item_id, item_name, price, currency)
 *   - Collection/category view → view_item_list (with collection name)
 *   - Cart drawer open → view_cart (cart contents from cart.js API)
 *   - Cart page view → view_cart
 *   - Add-to-cart form submit → add_to_cart (with product, quantity,
 *     price)
 *   - Cart line item removal → remove_from_cart
 *   - Checkout link click → begin_checkout (sometimes; depends on theme)
 *
 * What it does NOT capture (different concerns, different layers):
 *   - Purchase events (Web Pixel API extension, /thank_you redirect)
 *   - Identity stitching (lbmgb-tracking.liquid handles customer.email)
 *   - Email signup (PW.track.emailSignup or equivalent in brand theme)
 *   - Notify-me-when-back-in-stock (separate hook; see ADR-4.22)
 *
 * Brand-extensible custom dimensions:
 *   - Reads window.LBMGB.config.brandCustomDims (object) and merges its
 *     keys into every event payload. So a wine brand can pass
 *     {vintage, region, varietal} without us hardcoding wine schema.
 *
 * Per ADR-4.21: events flow through LBMGB.track.* which handles consent
 * gating + dataLayer push + Klaviyo dual-call. Auto-track is the
 * "where to fire from" layer; LBMGB.track is the "how to route" layer.
 */
(function () {
  'use strict';

  // ----------------------------------------------------------------------
  // Boot guards
  // ----------------------------------------------------------------------

  function debug() {
    if (window.LBMGB && window.LBMGB.config && window.LBMGB.config.debug) {
      var args = ['[LBMGB auto-track]'].concat([].slice.call(arguments));
      console.log.apply(console, args);
    }
  }

  function getCfg(path, fallback) {
    var ref = window.LBMGB && window.LBMGB.config;
    if (!ref) return fallback;
    var keys = path.split('.');
    for (var i = 0; i < keys.length; i++) {
      if (ref == null) return fallback;
      ref = ref[keys[i]];
    }
    return ref != null ? ref : fallback;
  }

  function brandCustomDims() {
    var d = getCfg('brandCustomDims', null);
    return d && typeof d === 'object' ? d : {};
  }

  // Shopify's storefront-standard ProductJson element. Themes that ship
  // with the official Dawn theme (and most agency-built themes) emit:
  //   <script id="ProductJson-{section}" type="application/json">
  //     { ...product object... }
  //   </script>
  // We grab the first one we find on the page; if none, we fall back to
  // OpenGraph product meta tags (also Shopify-standard).
  function getProductJson() {
    var scripts = document.querySelectorAll('script[type="application/json"][id*="ProductJson"]');
    for (var i = 0; i < scripts.length; i++) {
      try { return JSON.parse(scripts[i].textContent); } catch (_) {}
    }
    // Fallback: Shopify outputs product info as JSON-LD on PDPs.
    var jsonLd = document.querySelectorAll('script[type="application/ld+json"]');
    for (var j = 0; j < jsonLd.length; j++) {
      try {
        var parsed = JSON.parse(jsonLd[j].textContent);
        var arr = Array.isArray(parsed) ? parsed : [parsed];
        for (var k = 0; k < arr.length; k++) {
          if (arr[k] && arr[k]['@type'] === 'Product') return arr[k];
        }
      } catch (_) {}
    }
    return null;
  }

  // Normalize whatever shape we got into the GA4-standard `items[]` form.
  function productToItem(p, qty) {
    if (!p) return null;
    qty = qty || 1;
    var price = null;
    // Shopify ProductJson: price is in cents; OpenGraph: in price; JSON-LD:
    // in offers.price.
    if (typeof p.price === 'number') price = p.price / 100;
    else if (typeof p.price === 'string') price = parseFloat(p.price);
    else if (p.offers && p.offers.price) price = parseFloat(p.offers.price);
    var item = {
      item_id: p.id || p.product_id || (p.sku || p.handle || p.url || ''),
      item_name: p.title || p.name || '',
      item_brand: p.vendor || (p.brand && p.brand.name) || '',
      item_category: p.type || p.category || '',
      price: price,
      quantity: qty,
    };
    // Merge brand-extensible custom dimensions (e.g., vintage/region/varietal).
    var dims = brandCustomDims();
    Object.keys(dims).forEach(function (k) {
      // Don't overwrite GA4-standard keys.
      if (item[k] === undefined) item[k] = dims[k];
    });
    return item;
  }

  function track(method, params) {
    if (!window.LBMGB || !window.LBMGB.track || typeof window.LBMGB.track[method] !== 'function') {
      debug('LBMGB.track.' + method + ' not ready; skipping');
      return;
    }
    try { window.LBMGB.track[method](params); } catch (e) { debug('error firing', method, e); }
  }

  // ----------------------------------------------------------------------
  // Template detection — what page are we on?
  // ----------------------------------------------------------------------

  function getTemplate() {
    // Shopify exposes `window.Shopify.template` on most themes via the
    // theme.liquid `body` data-template attribute.
    var bodyTemplate = document.body.getAttribute('data-template');
    if (bodyTemplate) return bodyTemplate;
    if (window.Shopify && window.Shopify.template) return window.Shopify.template;
    // URL-based fallback for themes that don't expose the attribute.
    var path = location.pathname;
    if (/^\/products\//.test(path)) return 'product';
    if (/^\/collections\/[^/]+\/?$/.test(path)) return 'collection';
    if (/^\/cart\/?$/.test(path)) return 'cart';
    if (/^\/blogs\//.test(path)) return /\/[^/]+\/?$/.test(path) ? 'article' : 'blog';
    return 'unknown';
  }

  // ----------------------------------------------------------------------
  // PDP — view_item
  // ----------------------------------------------------------------------

  function firePdpViewItem() {
    var product = getProductJson();
    if (!product) {
      debug('PDP: no ProductJson / JSON-LD found; skipping view_item');
      return;
    }
    var item = productToItem(product, 1);
    track('viewItem', {
      currency: getCfg('defaultCurrency', 'USD'),
      value: item.price,
      items: [item],
    });
    debug('view_item fired for', item.item_id);
  }

  // ----------------------------------------------------------------------
  // Collection — view_item_list
  // ----------------------------------------------------------------------

  function fireCollectionViewItemList() {
    // Best-effort: capture collection slug from URL + count of products
    // on the page. Detailed item enumeration is theme-specific and
    // expensive to scrape; we fire a single event with the collection
    // identifier and let downstream platforms attribute the session.
    var match = location.pathname.match(/^\/collections\/([^/]+)/);
    var slug = match ? match[1] : 'unknown';
    var name = (document.querySelector('h1') && document.querySelector('h1').textContent.trim())
             || slug;
    track('viewItemList', {
      item_list_id: slug,
      item_list_name: name,
    });
    debug('view_item_list fired for', slug);
  }

  // ----------------------------------------------------------------------
  // Cart — view_cart
  // ----------------------------------------------------------------------

  function fireViewCart(reason) {
    // Use Shopify's cart.js API to get cart contents. Endpoint: /cart.js.
    fetch('/cart.js', { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (cart) {
        var items = (cart.items || []).map(function (it) {
          return {
            item_id: String(it.product_id || it.id || ''),
            item_name: it.product_title || it.title || '',
            item_variant: it.variant_title || '',
            item_brand: it.vendor || '',
            price: it.final_price ? it.final_price / 100 : null,
            quantity: it.quantity || 1,
          };
        });
        track('viewCart', {
          currency: cart.currency || getCfg('defaultCurrency', 'USD'),
          value: cart.total_price ? cart.total_price / 100 : 0,
          items: items,
          source: reason || 'unknown',
        });
        debug('view_cart fired', { reason: reason, item_count: items.length });
      })
      .catch(function (e) { debug('view_cart fetch failed', e); });
  }

  // ----------------------------------------------------------------------
  // Add-to-cart — form submit interception
  // ----------------------------------------------------------------------

  function attachAddToCartListener() {
    // Shopify's standard add-to-cart form has action="/cart/add" or
    // action containing "/cart/add". We listen on document for delegated
    // submits so dynamically-loaded forms work too.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var action = form.getAttribute('action') || '';
      if (!/\/cart\/add/.test(action)) return;

      // Capture product context before the form submits.
      var product = getProductJson();
      var qtyInput = form.querySelector('input[name="quantity"]');
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      var item = productToItem(product, qty);
      if (!item) {
        debug('add_to_cart: no product context; firing minimal event');
        item = { item_id: 'unknown', item_name: 'unknown', quantity: qty };
      }
      track('addToCart', {
        currency: getCfg('defaultCurrency', 'USD'),
        value: (item.price || 0) * qty,
        items: [item],
      });
      debug('add_to_cart fired for', item.item_id);
    }, true); // capture phase so we run before Shopify's own handler
  }

  // ----------------------------------------------------------------------
  // Cart drawer open — view_cart
  // ----------------------------------------------------------------------

  function attachCartDrawerListener() {
    // Many themes implement the cart drawer as a custom element <cart-drawer>
    // or as a CSS class toggle (.is-open / .drawer--open / etc.). We watch
    // the body for class mutations + listen for clicks on cart-trigger
    // elements as a backup.
    var fired = false;
    var resetTimer = null;

    function onMaybeOpened() {
      if (fired) return;
      fired = true;
      fireViewCart('drawer_open');
      // Reset after 5s so re-opens count as new sessions.
      if (resetTimer) clearTimeout(resetTimer);
      resetTimer = setTimeout(function () { fired = false; }, 5000);
    }

    // Selector overrides via config so brand themes with non-standard
    // markup can plug in.
    var cartTriggerSelectors = getCfg('autoTrack.cartTriggerSelectors', [
      '[data-cart-toggle]',
      '[data-action="open-cart"]',
      '.header__icon--cart',
      'a[href="/cart"]',
      '#cart-icon-bubble',
    ]);

    document.addEventListener('click', function (e) {
      var target = e.target.closest(cartTriggerSelectors.join(','));
      if (!target) return;
      // Defer fireViewCart slightly so the drawer's own open animation runs first.
      setTimeout(onMaybeOpened, 100);
    });
  }

  // ----------------------------------------------------------------------
  // Begin checkout — link click interception
  // ----------------------------------------------------------------------

  function attachBeginCheckoutListener() {
    // Standard Shopify checkout button: <a href="/checkout"> or form
    // action="/cart" with name="checkout" submit. Web Pixel API extension
    // also captures begin_checkout, so this is dedup-via-event-id territory.
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href*="/checkout"], button[name="checkout"]');
      if (!a) return;
      // Get cart contents quickly (best-effort; fire-and-forget).
      fetch('/cart.js', { credentials: 'include' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          var items = (cart.items || []).map(function (it) {
            return {
              item_id: String(it.product_id || it.id || ''),
              item_name: it.product_title || it.title || '',
              price: it.final_price ? it.final_price / 100 : null,
              quantity: it.quantity || 1,
            };
          });
          track('beginCheckout', {
            currency: cart.currency || getCfg('defaultCurrency', 'USD'),
            value: cart.total_price ? cart.total_price / 100 : 0,
            items: items,
          });
          debug('begin_checkout fired (theme)');
        })
        .catch(function () {});
    }, true);
  }

  // ----------------------------------------------------------------------
  // Boot — wire everything based on the current page template
  // ----------------------------------------------------------------------

  function boot() {
    if (!window.LBMGB || !window.LBMGB.track) {
      // lbmgb-track.js hasn't loaded yet (it's deferred). Retry shortly.
      setTimeout(boot, 200);
      return;
    }

    var template = getTemplate();
    debug('booting on template:', template);

    // Always-on listeners (cart drawer can open from any page).
    attachAddToCartListener();
    attachCartDrawerListener();
    attachBeginCheckoutListener();

    // Page-specific fires.
    if (template === 'product') {
      firePdpViewItem();
    } else if (template === 'collection') {
      fireCollectionViewItemList();
    } else if (template === 'cart') {
      fireViewCart('cart_page');
    }
    // article/blog/index/etc.: no auto-fire; Enhanced Measurement covers
    // page_view + scroll + form events; PW.track.emailSignup covers
    // newsletter forms.

    debug('auto-track active');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
